import React, { lazy, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTeam } from '../contexts/TeamContext';
import PlayerView from '../pages/PlayerView';

// Apart geladen: wie alleen de opstelling bekijkt, haalt scorebord en tijdlijn niet op.
const LiveVolgen = lazy(() => import('../pages/LiveVolgen'));

interface TeamPlayerViewProps {
  /** De volgpagina van het live verslag in plaats van de opstelling. */
  live?: boolean;
}

export const TeamPlayerView: React.FC<TeamPlayerViewProps> = ({ live = false }) => {
  const { t } = useTranslation('playerView');
  const { slug, publicId, matchId } = useParams<{ slug: string; publicId?: string; matchId?: string }>();
  const { setCurrentTeamBySlug, setCurrentTeamByPublicId, currentTeam, loading, error } = useTeam();
  const [initializing, setInitializing] = useState(true);

  // Add noindex meta tag for SEO protection
  useEffect(() => {
    const metaTag = document.createElement('meta');
    metaTag.name = 'robots';
    metaTag.content = 'noindex, nofollow';
    document.head.appendChild(metaTag);

    return () => {
      document.head.removeChild(metaTag);
    };
  }, []);

  useEffect(() => {
    // New secure format: /team/publicId/slug
    if (publicId && slug) {
      if (!currentTeam || currentTeam.publicId !== publicId || currentTeam.slug !== slug) {
        setCurrentTeamByPublicId(publicId, slug)
          .catch(() => {
            // Error handling is done in the context
          })
          .finally(() => {
            setInitializing(false);
          });
      } else {
        setInitializing(false);
      }
    }
    // Legacy format: /team/slug (backwards compatibility)
    else if (slug && !publicId) {
      if (!currentTeam || currentTeam.slug !== slug) {
        setCurrentTeamBySlug(slug)
          .catch(() => {
            // Error handling is done in the context
          })
          .finally(() => {
            setInitializing(false);
          });
      } else {
        setInitializing(false);
      }
    } else {
      setInitializing(false);
    }
  }, [slug, publicId, currentTeam, setCurrentTeamBySlug, setCurrentTeamByPublicId]);

  // Loading state
  if (loading || initializing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-hockey-blue mx-auto mb-4"></div>
          <p className="text-gray-600">{t('loading.team')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !currentTeam) {
    // Oude links van vóór de publicId hebben de vorm /team/:slug. Die worden sinds de
    // AVG-fix niet meer publiek ondersteund, want een raadbare slug gaf toegang tot de
    // volledige spelerslijst. Geef daar een begrijpelijke uitleg in plaats van "niet
    // gevonden" - de link is niet kapot, hij is ingetrokken.
    const isVerlopenLegacyLink = !publicId && !!slug;

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="text-red-600 mb-4">
            <svg className="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.5 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {isVerlopenLegacyLink
              ? t('errors.expiredLinkTitle')
              : t('errors.teamNotFoundTitle')}
          </h1>
          <p className="text-gray-600 mb-4">
            {/* `error` komt uit TeamContext en is een backend-melding: die blijft
                Nederlands, net als de rest van de backend. */}
            {isVerlopenLegacyLink
              ? t('errors.expiredLinkBody')
              : error ||
                t('errors.teamNotFoundBody', {
                  team: publicId ? `${publicId}/${slug}` : slug,
                })}
          </p>
          <a
            href="/"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-hockey-blue hover:bg-hockey-blue-pressed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hockey-blue"
          >
            {t('errors.toHomepage')}
          </a>
        </div>
      </div>
    );
  }

  // If we have a team but it's not the one from the URL, 
  // this means the team loading is still in progress
  if (currentTeam) {
    // For new secure format, check both publicId and slug
    if (publicId && slug && (currentTeam.publicId !== publicId || currentTeam.slug !== slug)) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-hockey-blue mx-auto mb-4"></div>
            <p className="text-gray-600">{t('loading.team')}</p>
          </div>
        </div>
      );
    }
    // For legacy format, only check slug
    else if (!publicId && slug && currentTeam.slug !== slug) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-hockey-blue mx-auto mb-4"></div>
            <p className="text-gray-600">{t('loading.team')}</p>
          </div>
        </div>
      );
    }
  }

  if (live) {
    return <LiveVolgen matchId={matchId ? parseInt(matchId, 10) : undefined} />;
  }

  // Render the player view with the correct team context
  // Pass matchId if provided in URL
  return <PlayerView matchId={matchId ? parseInt(matchId, 10) : undefined} />;
};